import AppLink, { AppLinkProps } from "@/components/AppLink";
import Button, { ButtonProps } from "@/components/Button";
import { SVGArrowRight } from "@/lib/svg";
import { useMemo, useRef } from "react";
import ClientCSSTransition from "@/lib/components/ClientCSSTransition";
import { Container, OpenIcon, LinkWrapper, CloseIcon } from "./styles";

export type HotspotProps = {
  _key: string;
  x: number | null;
  y: number | null;
  isExternal: boolean | null;
  externalLink: {
    url: string;
    blank: boolean;
  } | null;
  internalLink: AppLinkProps | null;
  details: string;
  isActive: boolean;
  setActive: () => void;
  setInactive: () => void;
};

const buttonProps: ButtonProps = {
  as: "a",
  variant: "primaryBoxed",
  icon: <SVGArrowRight />,
};

const Hotspot: React.FC<HotspotProps> = (props) => {
  const {
    x,
    y,
    isExternal,
    externalLink,
    internalLink,
    details,
    isActive,
    setActive,
    setInactive,
  } = props;

  const linkWrapperRef = useRef<HTMLDivElement>(null);

  const link = useMemo(() => {
    if (isExternal && externalLink?.url) {
      return (
        <Button
          {...buttonProps}
          href={externalLink?.url}
          target={`${externalLink?.blank ? "_blank" : ""}`}
        >
          {details || "Show"}
        </Button>
      );
    } else if (!isExternal && internalLink?.page) {
      return (
        <AppLink page={internalLink.page} params={internalLink.params}>
          <Button {...buttonProps}>{details}</Button>
        </AppLink>
      );
    }
  }, [isExternal, externalLink, internalLink, details]);

  if (!x || !y) return null;
  const position = {
    x,
    y,
  };

  return (
    <>
      <ClientCSSTransition
        in={isActive}
        classNames="animation"
        nodeRef={linkWrapperRef}
        unmountOnExit
        mountOnEnter
        addEndListener={(done) => {
          linkWrapperRef.current?.addEventListener(
            "transitionend",
            done,
            false
          );
        }}
      >
        <LinkWrapper ref={linkWrapperRef} $position={position}>
          {link}
        </LinkWrapper>
      </ClientCSSTransition>
      <Container
        $position={position}
        onClick={() => (isActive ? setInactive() : setActive())}
      >
        {isActive ? <CloseIcon /> : <OpenIcon />}
      </Container>
    </>
  );
};

export default Hotspot;
